import type { Camera } from '@playplate/shared';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CameraStatusPill, EmptyState, Loading, PageHeader, Panel } from '../components/ui';
import { api, ApiError } from '../lib/api';

/**
 * Live view over WebRTC.
 *
 * The browser never learns anything about the camera except its id. It builds an
 * SDP offer, the API checks whether this role may watch that zone, and only then
 * forwards the offer to go2rtc — which holds the RTSP URL and credentials
 * server-side. There is no stream URL in this page to leak, copy or guess.
 */
export function LiveViewPage() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('camera');

  const camerasQuery = useQuery({
    queryKey: ['cameras', 'list'],
    queryFn: () => api.get<{ items: Camera[] }>('/cameras'),
  });

  if (camerasQuery.isLoading) return <Loading label="Loading cameras" />;

  const cameras = (camerasQuery.data?.items ?? []).filter((c) => c.enabled);
  const selected = cameras.find((c) => c.id === selectedId) ?? cameras[0] ?? null;

  if (cameras.length === 0) {
    return (
      <div>
        <PageHeader title="Live View" />
        <EmptyState
          title="No cameras available"
          message="Cameras that are disabled or outside your role's zones are not shown here."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Live View"
        subtitle="Streams are proxied server-side; camera credentials never reach the browser."
      />

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <Panel title="Cameras">
          <ul className="max-h-[70vh] divide-y divide-line/50 overflow-y-auto">
            {cameras.map((camera) => (
              <li key={camera.id}>
                <button
                  type="button"
                  onClick={() => setParams({ camera: camera.id })}
                  className={`w-full px-4 py-3 text-left transition-colors hover:bg-panel-raised ${
                    selected?.id === camera.id ? 'bg-accent/10' : ''
                  }`}
                >
                  <p className="truncate text-sm font-semibold">{camera.name}</p>
                  <p className="truncate text-xs text-muted">{camera.location}</p>
                  <div className="mt-1.5">
                    <CameraStatusPill status={camera.status} fps={camera.currentFps} />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        {selected ? <LivePlayer key={selected.id} camera={selected} /> : null}
      </div>
    </div>
  );
}

type PlayerState = 'idle' | 'connecting' | 'playing' | 'failed';

function LivePlayer({ camera }: { camera: Camera }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const connectionRef = useRef<RTCPeerConnection | null>(null);
  const [state, setState] = useState<PlayerState>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function connect() {
      setState('connecting');
      setError(null);

      const connection = new RTCPeerConnection({
        // No STUN/TURN: both ends are on the store LAN, and adding public STUN
        // servers would leak the network's existence to a third party for no
        // benefit.
        iceServers: [],
      });
      connectionRef.current = connection;

      connection.addTransceiver('video', { direction: 'recvonly' });

      connection.ontrack = (event) => {
        if (videoRef.current && event.streams[0]) {
          videoRef.current.srcObject = event.streams[0];
        }
      };

      connection.onconnectionstatechange = () => {
        if (cancelled) return;
        if (connection.connectionState === 'connected') setState('playing');
        if (connection.connectionState === 'failed' || connection.connectionState === 'disconnected') {
          setState('failed');
          setError('The live stream dropped. The camera may be offline.');
        }
      };

      try {
        const offer = await connection.createOffer();
        await connection.setLocalDescription(offer);

        // Wait for ICE gathering: go2rtc expects a complete offer, and
        // trickling into a one-shot HTTP exchange has nowhere to go.
        await new Promise<void>((resolve) => {
          if (connection.iceGatheringState === 'complete') return resolve();
          const check = () => {
            if (connection.iceGatheringState === 'complete') {
              connection.removeEventListener('icegatheringstatechange', check);
              resolve();
            }
          };
          connection.addEventListener('icegatheringstatechange', check);
          // Do not hang forever if a candidate never resolves.
          setTimeout(resolve, 2000);
        });

        const answer = await api.post<{ sdp: string; type: string }>(
          `/cameras/${camera.id}/webrtc`,
          { sdp: connection.localDescription?.sdp ?? offer.sdp },
        );

        if (cancelled) return;
        await connection.setRemoteDescription({ type: 'answer', sdp: answer.sdp });
      } catch (e) {
        if (cancelled) return;
        setState('failed');
        setError(
          e instanceof ApiError
            ? e.status === 503
              ? 'Live view service is unavailable. Detection and alerting are unaffected.'
              : e.message
            : 'Could not start the live stream.',
        );
      }
    }

    void connect();

    return () => {
      cancelled = true;
      connectionRef.current?.close();
      connectionRef.current = null;
    };
  }, [camera.id]);

  return (
    <Panel
      title={`${camera.name} — ${camera.location}`}
      actions={<CameraStatusPill status={camera.status} fps={camera.currentFps} />}
    >
      <div className="relative aspect-video bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="h-full w-full object-contain"
          aria-label={`Live view of ${camera.name}`}
        />

        {state !== 'playing' ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink/80 text-center">
            {state === 'failed' ? (
              <>
                <AlertTriangle className="h-6 w-6 text-high" aria-hidden />
                <p className="max-w-sm px-6 text-sm text-muted">{error}</p>
              </>
            ) : (
              <>
                <Loader2 className="h-6 w-6 animate-spin text-muted" aria-hidden />
                <p className="text-sm text-muted">Connecting to the stream…</p>
              </>
            )}
          </div>
        ) : null}
      </div>

      <p className="border-t border-line px-4 py-2.5 text-xs text-muted">
        Viewing is recorded in the audit log. No facial recognition is performed on any stream.
      </p>
    </Panel>
  );
}
